<template>
  <ButtonBase
    class="metamask-btn-large"
    @click="handleButtonClick"
    v-if="metamaskInstalled && props.isLargeButton"
  >
    <MetmaskIcon />{{ $t('MetamaskButton.label.connect') }}
  </ButtonBase>
  <button
    class="metamask-btn"
    @click="handleButtonClick"
    v-else-if="metamaskInstalled"
  >
    <MetmaskIcon />
  </button>
</template>

<script setup>
import { onMounted, computed, ref } from "vue";
import MetmaskIcon from "@/components/icons/Metamask.vue";
import * as EvmChains from "@/assets/data/evm_chains.json";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";

import {
  getInstalledMetamaskProvider,
  requestChangeNetwork,
} from '@/composables/metamask/useMetamaskProvider';

const emit = defineEmits(["updateWallet"]);
const metamaskProvider = ref({});
const isMobile = ref(false);

const mmSdkUrl = "https://c0f4f41c-2f55-4863-921b-sdk-docs.github.io/cdn/metamask-sdk.js";

const addMetamaskSdkScriptElement = () => {
  const mmSdkScript = document.createElement("script");
  mmSdkScript.setAttribute("src", mmSdkUrl);
  document.head.appendChild(mmSdkScript);

  return mmSdkScript;
}

const props = defineProps({
  evmChainId: {
    type: Number,
    required: true,
    default: 1,
  },
  isLargeButton: {
    type: Boolean,
    required: false,
    default: false
  }
});

const metamaskInstalled = computed(() => {
  return metamaskProvider.value && metamaskProvider.value?.isMetaMask;
});

const handleAccountsChangedEvent = async (metaMaskAccount) => {
  const metaMaskWalletAddresss = metaMaskAccount[0];
  await requestChangeNetwork(props.evmChainId);
  emit('update-wallet', {
    walletAddress: metaMaskWalletAddresss,
    isMobileFlow: isMobile.value,
  });
};

const requestMetaMaskAccount = async () => {
  await metamaskProvider.value
    .request({ method: "eth_requestAccounts" })
    .then(handleAccountsChangedEvent)
    .catch((error) => {
      if (error.code === 4001) {
        // EIP-1193 userRejectedRequest error
        console.log("Please connect to MetaMask.");
      } else {
        console.error(error);
      }
    });
};

const handleButtonClick = async () => {
  await requestMetaMaskAccount();
};

onMounted(() => {
  addMetamaskSdkScriptElement().onload = async function () {
    const mmProvider = await getInstalledMetamaskProvider();
    if (mmProvider) {
      metamaskProvider.value = mmProvider;
      return;
    }

    // We download the Metamask SDK here when Metamask is not installed in the browser
    // The Metamask SDK cannot be installed as one of the node_modules due to dependency issues, therefore we download it from CDN
    // The SDK will pop-up an instruction to install Metamask extension or a QR code for customers to scan with their Metamask mobile phone
    // Metamask SDK options, see https://docs.metamask.io/wallet/reference/sdk-js-options/
    //
    // Notes to developers: Metamask Mobile is not working with localhost because it doesn't
    // inject any provider for localhost. To test Metamask Mobile,
    // you need to deploy to dev or staging environment.
    isMobile.value = true;
    const options = {
      forceInjectProvider: true,
      forceDeleteProvider: true,
    }

    const MMSDK = new MetaMaskSDK.MetaMaskSDK(options);
    const ethereum = MMSDK.getProvider();
    metamaskProvider.value = ethereum;
  };
});

</script>

<style lang="scss">
.metamask-btn {
  width: 6rem;
  aspect-ratio: 1 / 1;
  background: var(--input-background);
  border-radius: $border-radius;
  cursor: pointer;
  position: relative;
  border: 2px solid rgb(246, 133, 27, 0.25);
  transition: background 250ms;

  &:hover {
    background: #f6851b87;
  }

  &:active {
    background: rgb(255, 231, 208);
  }

  svg {
    width: 2.5rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
.metamask-btn-large {
  background: var(--input-background);
  border-radius: $border-radius;
  cursor: pointer;
  position: relative;
  border: 2px solid rgb(246, 133, 27, 0.25);
  transition: background 250ms;
  background-color: #f6851b9e;

  &:hover {
    background: rgb(255, 231, 208);
  }

  svg {
    width: 2.5rem;
    position: absolute;
    top: 50%;
    left: 15%;
    transform: translate(-50%, -50%);
  }
}
</style>
